function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useState, useMemo, useCallback } from 'react';
import { pathComparator } from '@truqc/content-path';
import saveFormBehavior from '../../../../nativeBehaviors/saveFormBehavior';
import store from '../../../../reducers/storeRegistration';
import logger from '../../../../utils/logger';
import useGenerateChecks from './useGenerateChecks';

/**
 * Custom hook for checks settings modal state and handlers.
 * Extracts logic from ChecksSettingsModal to reduce component size.
 */
function useChecksSettings({
  checksCollection = {},
  defaultChecks = {},
  path,
  formActions,
  reduxEntity,
  content = null,
  notificationActions,
  pendingOperation = false,
  onClose
}) {
  const [currentView, setCurrentView] = useState('list');
  const [userRequest, setUserRequest] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [addingId, setAddingId] = useState(null);
  const mergedChecks = useMemo(() => {
    const merged = _objectSpread({}, checksCollection);
    Object.entries(defaultChecks || {}).forEach(([id, defaultCheck]) => {
      if (!merged[id]) {
        merged[id] = _objectSpread(_objectSpread({}, defaultCheck), {}, {
          enabled: false,
          isDefault: true
        });
      }
    });
    return merged;
  }, [checksCollection, defaultChecks]);
  const checksArray = useMemo(() => {
    const arr = Object.entries(mergedChecks || {}).map(([id, check]) => _objectSpread({
      id
    }, check));
    return arr.sort(pathComparator('_created'));
  }, [mergedChecks]);
  const handleGenerate = useGenerateChecks({
    checksArray,
    content,
    setSuggestions,
    setIsGenerating,
    notificationActions,
    pendingOperation
  });
  const handleToggle = useCallback(async (checkId, newEnabled) => {
    try {
      const state = store.getState();
      const {
        active
      } = state[reduxEntity];
      if (newEnabled && defaultChecks[checkId] && !checksCollection[checkId]) {
        const defaultCheck = defaultChecks[checkId];
        await formActions.change(active.id, `content${path}/settings/checks/${checkId}`, _objectSpread(_objectSpread({}, defaultCheck), {}, {
          enabled: true,
          isDefault: true
        }));
      } else {
        await formActions.change(active.id, `content${path}/settings/checks/${checkId}/enabled`, newEnabled);
      }
    } catch (error) {
      logger('Error toggling check:', error);
      notificationActions.info({
        message: 'Failed to update check setting. Please try again.'
      }, {
        autoClose: 5000
      });
    }
  }, [defaultChecks, checksCollection, formActions, path, reduxEntity, notificationActions]);
  const handleDelete = useCallback(async checkId => {
    try {
      const state = store.getState();
      const {
        active
      } = state[reduxEntity];
      if (checksCollection[checkId]) {
        await formActions.change(active.id, `content${path}/settings/checks/${checkId}`, null);
      }
    } catch (error) {
      logger('Error deleting check:', error);
      notificationActions.info({
        message: 'Failed to delete check. Please try again.'
      }, {
        autoClose: 5000
      });
    }
  }, [checksCollection, formActions, path, reduxEntity, notificationActions]);
  const handleGenerateClick = useCallback(async () => {
    await handleGenerate(userRequest);
  }, [handleGenerate, userRequest]);
  const handleAddSuggestion = useCallback(async suggestion => {
    setAddingId(suggestion.id);
    try {
      const state = store.getState();
      const {
        active
      } = state[reduxEntity];
      const newCheckId = suggestion.id || `custom_${Date.now()}`;
      const newCheck = {
        query: suggestion.query,
        description: suggestion.description,
        category: suggestion.category || 'general',
        severity: suggestion.severity || 'medium',
        isDefault: false,
        enabled: true,
        _created: Date.now()
      };
      await formActions.change(active.id, `content${path}/settings/checks/${newCheckId}`, newCheck);
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
    } catch (error) {
      logger('Error adding suggestion:', error);
      notificationActions.info({
        message: 'Failed to add check. Please try again.'
      }, {
        autoClose: 5000
      });
    } finally {
      setAddingId(null);
    }
  }, [formActions, path, reduxEntity, notificationActions]);
  const handleClose = useCallback(async () => {
    try {
      // eslint-disable-next-line import/no-named-as-default-member
      await saveFormBehavior.evaluate({}, reduxEntity);
    } catch (error) {
      logger('Error saving form on modal close:', error);
      notificationActions.info({
        message: 'Unable to save changes. Please try again.'
      }, {
        autoClose: 5000
      });
    } finally {
      setCurrentView('list');
      setUserRequest('');
      setSuggestions([]);
      onClose();
    }
  }, [reduxEntity, onClose, notificationActions]);
  const handleNavigateToGenerate = useCallback(() => {
    setCurrentView('generate');
  }, []);
  const handleNavigateToList = useCallback(() => {
    setCurrentView('list');
  }, []);
  return {
    currentView,
    checksArray,
    userRequest,
    setUserRequest,
    isGenerating,
    suggestions,
    addingId,
    handleToggle,
    handleDelete,
    handleGenerateClick,
    handleAddSuggestion,
    handleClose,
    handleNavigateToGenerate,
    handleNavigateToList
  };
}
export default useChecksSettings;